const PRACTICE_SENTENCE_MAX_CHARS = 280;
const PRACTICE_WORD_MAX_CHARS = 80;
const PRACTICE_WORD_PATTERN = /^[\p{L}\p{M}]+(?:[-'’][\p{L}\p{M}]+)*$/u;
const LINKED_WORD_PRACTICE_MARKER = "[LINKED_WORD_PRACTICE_V1]";

export type PracticeLanguageLevel = "absolute_beginner" | "beginner" | "intermediate" | "advanced";

export function normalizePracticeSentence(value: unknown): string {
  if (typeof value !== "string") return "";

  return value
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, PRACTICE_SENTENCE_MAX_CHARS);
}

export function normalizePracticeWord(value: unknown): string {
  if (typeof value !== "string") return "";

  const word = value
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .normalize("NFC");

  return word.length <= PRACTICE_WORD_MAX_CHARS && PRACTICE_WORD_PATTERN.test(word) ? word : "";
}

export function buildPracticeWordInstructions(value: unknown): string | null {
  const word = normalizePracticeWord(value);

  if (!word) return null;

  return [
    `[LINKED_WORD_PRACTICE_V1] Linked word: "${word}".`,
    "The word may be in either language. Infer its target-language form and instruction-language meaning.",
    "Introduce both, then give one short level-appropriate instruction-language sentence using that meaning and ask the learner to translate it.",
    "Do not reveal the target-language sentence until the learner answers.",
    "Then correct the answer, say the natural sentence, ask for repetition, and give brief pronunciation feedback.",
  ].join(" ");
}

export function isLinkedWordPracticeInstructions(value: unknown): value is string {
  return typeof value === "string" && value.trimStart().startsWith(LINKED_WORD_PRACTICE_MARKER);
}

export function resolveEffectiveCustomInstructions(
  initialCustomInstructions: string | null | undefined,
  savedCustomInstructions: string
): string {
  return initialCustomInstructions?.trim() || savedCustomInstructions;
}

export function resolveEffectivePracticeLanguageLevel({
  userOverride,
  linkedLevel,
  savedLevel,
}: {
  userOverride: PracticeLanguageLevel | null;
  linkedLevel: PracticeLanguageLevel | null;
  savedLevel: PracticeLanguageLevel;
}): PracticeLanguageLevel {
  return userOverride ?? linkedLevel ?? savedLevel;
}

export function buildPracticeSentenceInstructions(value: unknown): string | null {
  const sentence = normalizePracticeSentence(value);

  if (!sentence) return null;

  return [
    `Practice this exact target-language sentence: "${sentence}".`,
    "Start by saying the sentence naturally.",
    "Briefly explain what it means in simple English.",
    "Ask the learner to repeat it aloud.",
    "Give pronunciation feedback and one short follow-up that uses the same sentence.",
    "Keep the session focused on this sentence unless the learner asks to move on.",
  ].join(" ");
}

export function buildTranslateSentenceInstructions(value: unknown): string | null {
  const sentence = normalizePracticeSentence(value);

  if (!sentence) return null;

  return [
    `Practice translating this instruction-language sentence into the selected target language: "${sentence}".`,
    "Do not give the target-language answer first.",
    "Ask the learner to say the sentence in the target language.",
    "Then correct it into one natural target-language version.",
    "Have the learner repeat the corrected sentence aloud.",
    "Briefly explain one useful grammar, word-order, or pronunciation point.",
    "Keep the session focused on this translation unless the learner asks to move on.",
  ].join(" ");
}
